import { AuditlogList } from "components/AuditlogList/AuditlogList";
import { BooksSoftwareSubscriptionsModal } from "components/BooksSoftwareSubscriptionsModal";
import DashboardContainer from "components/dashboard/DashboardContainer";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { Divider } from "components/design/Divider";
import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Avatar } from "components/DesignSystem/AvatarGroup/Avatar";
import { Button } from "components/DesignSystem/Button/Button";
import Card from "components/DesignSystem/Card/Card";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { KeyDownWrapper } from "components/DesignSystem/KeydownWrapper/KeydownWrapper";
import Modal from "components/DesignSystem/Modal/Modal";
import Slider from "components/DesignSystem/Slider/Slider";
import Tab from "components/DesignSystem/Tab/Tab";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { Label } from "components/DesignSystem/TextInput/TextInput";
import { Cross } from "components/icons/Cross";
import { DiamondPurple } from "components/icons/DiamondPurple";
import { TriangleWarning } from "components/icons/TriangleWarning";
import { EditJournalEntry } from "components/JournalEntry/EditJournalEntry";
import { LinkInkleBillModal } from "components/LinkInkleBillModal/LinkInkleBillModal";
import { LinkInkleInvoiceModal } from "components/LinkInkleInvoiceModal/LinkInkleInvoiceModal";
import { LockedUi } from "components/LockedUi/LockedUi";
import { PermissionBasedUI } from "components/PermissionBasedUI/PermissionBasedUI";
import { DeleteJournalEntry } from "components/Transaction/Slider/DeleteJournalEntry";
import { AUTO_MATCHED } from "constants/invoiceTransactionMappingStatus";
import { FINANCIAL_RECORD_TYPE } from "constants/vendorBills";
import { CLASSIFICATION_FEATURE_ACCESS } from "constants/subscriptionPermissionFeatures";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useGetTransaction } from "hooks/useGetTransaction";
import { useModal } from "hooks/useModal";
import { useUpdateQuery } from "hooks/useQuery";
import { useToast } from "hooks/useToast";
import { ModalDescriptionWrapper } from "pages/Books/RevenueMetrics/RevenueMetricsLocked";
import { ReactNode } from "react";
import { useDispatch, useSelector } from "react-redux";
import ThreeDots from "static/images/ThreeDots.svg";
import {
  useGetMappedBillQuery,
  useGetMappedInvoiceQuery,
  useUnlinkInvoicesMutation,
} from "store/apis/transactions";
import { closeSlider, openSlider } from "store/slices/transactions";
import { openEditSplitTransactionModal } from "store/slices/splitTransaction";
import { RootState } from "store/store";
import { Transaction, Transactions } from "types/Models/books";
import { BackendError } from "types/utils/error";
import { Classifications } from "./Classifications";
import { Comments } from "./Comment";
import { DeleteTransaction } from "./DeleteTransaction";
import { Invoices } from "./Invoice";
import { InvoiceCategoryDetails } from "./InvoiceCategoryDetails";
import { LinkedBills } from "./LinkedBills";
import { LinkedInvoices } from "./LinkedInvoices";
import { Memo } from "./Memo";
import { Category, TransactionInfo } from "./TransactionInfo";
import { SplitTransactionDetail } from "./SplitTransactionDetail";
import { SplitIcon } from "components/icons/SplitIcon";
import { Link } from "components/icons/Link";
import { DISPLAY_DATE } from "constants/date";
import dayjs from "dayjs";
import { SplitTransaction } from "../SplitTransaction/SplitTransaction";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { WRITTEN_OFF_UNLINK_DISABLED_TOOLTIP } from "constants/invoicing";
import { ActionDropdown } from "../ActionDropdown";
import * as Popover from "@radix-ui/react-popover";
import { AddCommentPop } from "components/AddComment/AddComment";
import { AddCommentPopoverRoot } from "components/AddCommentPopoverRoot/AddCommentPopoverRoot";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useTransactionActions } from "../useTransactionActions";
import { ChangeCategory } from "../UnLinkTransaction";
import { LinkBreak } from "components/icons/LinkBreak";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { Padlock } from "components/icons/Padlock";
import classNames from "classnames";

const Header = ({
  transactionId,
  ledgerEntryId,
  onDuplicate,
}: {
  transactionId: string;
  ledgerEntryId?: string;
  onDuplicate?: (transaction: Transaction) => void;
}) => {
  const dispatch = useDispatch();
  const { data: transactionDetail } = useGetTransaction({
    transactionId,
  });

  const { transaction: { logo, merchant, amount } = {} as Transaction } =
    transactionDetail || {};

  const {
    isOpen: isEditJournalEntryModalOpen,
    close: closeEditJournalEntryModal,
  } = useModal();

  const {
    isOpen: isDeleteJournalEntryModalOpen,
    close: closeDeleteJournalEntryModal,
  } = useModal();

  const hasLinkedInvoice = transactionDetail?.invoices?.some(
    ({ has_inkle_invoice }) => has_inkle_invoice
  );

  const { Action, modals } = useTransactionActions({
    transactionId,
    transactionData: transactionDetail,
    onDuplicate,
  });

  return (
    <>
      <div className="t-w-full t-px-5 t-py-3 t-border t-border-solid t-border-b t-border-l-0 t-border-t-0 t-border-r-0 t-border-neutral-0 t-bg-surface t-flex t-justify-between ">
        <div className="t-flex t-gap-2 t-items-center">
          {merchant && <Avatar src={logo || ""} alt={merchant!} size="large" />}
          <Slider.Title>
            <div className="t-text-subtitle-sm t-break-words t-max-w-32 t-line-clamp-2">
              {merchant || <AmountSuperScript amount={amount} />}
            </div>
          </Slider.Title>
        </div>
        <div className="t-flex t-gap-2 t-items-center">
          <LockedUi
            feature={WRITE_PERMISSION_KEYS.TRANSACTIONS}
            blockedUi={
              <div className="t-pointer-events-none">
                <Button size="small" customType="ghost_icon" disabled>
                  <Padlock />
                </Button>
              </div>
            }
          >
            <Dropdown.Root>
              <ConditionalToolTip
                condition={
                  hasLinkedInvoice &&
                  "Actions disabled for transaction linked to an invoice or bill"
                }
              >
                <Dropdown.Trigger asChild disabled={hasLinkedInvoice}>
                  <div>
                    <Button
                      size="small"
                      customType="ghost_icon"
                      disabled={hasLinkedInvoice}
                    >
                      <img
                        src={ThreeDots}
                        alt="Action"
                        className="t-select-none"
                      />
                    </Button>
                  </div>
                </Dropdown.Trigger>
              </ConditionalToolTip>

              <Dropdown.Portal>
                <Dropdown.Content
                  sideOffset={8}
                  side="bottom"
                  className="t-mr-16 !t-min-w-72"
                  align="start"
                >
                  <ActionDropdown
                    Action={Action}
                    transactionData={transactionDetail!}
                    showBaseActionItem={false}
                  />
                </Dropdown.Content>
              </Dropdown.Portal>
            </Dropdown.Root>
          </LockedUi>

          <Button
            customType="ghost_icon"
            size="small"
            onClick={() => {
              dispatch(closeSlider(transactionId));
            }}
          >
            <Cross color="currentColor" />
          </Button>
        </div>
      </div>
      <KeyDownWrapper>
        {modals}
        <DeleteJournalEntry
          ledgerEntryId={ledgerEntryId}
          transactionId={transactionId}
          close={closeDeleteJournalEntryModal}
          isOpen={isDeleteJournalEntryModalOpen}
        />
        {ledgerEntryId && (
          <EditJournalEntry
            ledgerEntryId={ledgerEntryId}
            close={closeEditJournalEntryModal}
            isOpen={isEditJournalEntryModalOpen}
          />
        )}
      </KeyDownWrapper>
    </>
  );
};

export const InfoItem = ({
  label,
  children,
}: {
  label: string | ReactNode;
  children: ReactNode;
}) => {
  return (
    <div className="t-flex t-flex-col t-gap-1">
      <Label className="!t-pb-0">{label}</Label>
      <span className="t-text-body-sm t-text-text-100">{children}</span>
    </div>
  );
};

const TransactionBody = ({
  sendToChat,
  transactionId,
  onDuplicate,
}: {
  sendToChat?: (() => void) | null;
  transactionId: string;
  onDuplicate?: (transaction: Transaction) => void;
}) => {
  const linkInkleInvoiceModal = useModal();
  const linkInkleBillModal = useModal();
  const changeCategoryModal = useModal();
  const dispatch = useDispatch();
  const linkInkleInvoiceModalEdit = useModal();
  const { isLoading, isSuccess } = useGetTransaction({
    transactionId,
  });

  const entityId = useCurrentEntityId();
  const group = useCurrentGroupContext();

  const { data: transaction } = useGetTransaction({
    transactionId,
  });

  const parentTransactionId = transaction?.parent_txn?.uuid || "";
  const { data: parentTransaction } = useGetTransaction({
    transactionId: parentTransactionId,
  });

  const {
    data: linkedInvoices,
    isLoading: isLinkedTxnDataLoading,
    isSuccess: isLinkedTxnDataSuccess,
  } = useGetMappedInvoiceQuery(
    {
      entityId,
      transactionId: transactionId,
    },
    {
      skip: !entityId || !transactionId,
    }
  );

  const hasLinkedBill = transaction?.invoices.some(
    ({ has_inkle_bill }) => has_inkle_bill
  );

  const {
    data: linkedBills,
    isLoading: isLinkedBillsLoading,
    isSuccess: isLinkedBillsSuccess,
  } = useGetMappedBillQuery(
    { entityId, transactionId },
    { skip: !entityId || !transactionId || !hasLinkedBill }
  );

  const isCreditCard = transaction?.transaction?.is_credit_card;

  const isSplitTransaction = Boolean(
    transaction?.split_transactions &&
      transaction?.split_transactions.length >= 0
  );

  const categoryName = transaction?.transaction?.category?.name ?? "";

  const isCredit =
    transaction?.transaction?.amount && transaction?.transaction.amount > 0;
  const isDebit =
    transaction?.transaction?.amount && transaction?.transaction.amount < 0;
  const isBillTransaction = Boolean(isDebit);

  const hasLinkedInvoice = transaction?.invoices.some(
    ({ has_inkle_invoice }) => has_inkle_invoice
  );

  const linkedInvoiceItems = (linkedInvoices?.invoices || []).filter(
    (inv) => inv.financial_record_type !== FINANCIAL_RECORD_TYPE.BILL
  );
  const linkedBillItems = (linkedBills?.invoices || []).filter(
    (inv) => inv.financial_record_type === FINANCIAL_RECORD_TYPE.BILL
  );
  const hasWrittenOffInvoice = linkedInvoiceItems.some((inv) =>
    Boolean(inv.written_off_amount)
  );

  const { alertToast, successToast } = useToast();

  const { link } = useConstructInternalLink();
  const [unlinkInvoices, { isLoading: unlinkingInvoice }] =
    useUnlinkInvoicesMutation();
  const confirmUnlinkInvoicesModal = useModal();
  const confirmUnlinkBillsModal = useModal();

  const onConfirmLinkInvoice = () => {
    changeCategoryModal.open();
  };

  const handleConfirmChangeCategory = () => {
    changeCategoryModal.close();
    linkInkleInvoiceModal.open();
  };

  const confirmUnlinkInvoices = async () => {
    try {
      await unlinkInvoices({
        entityId: entityId,
        financial_record_type:
          linkedInvoiceItems?.at(0)?.financial_record_type!,
        transactionId,
        groupId: group.uuid,
      }).unwrap();
      successToast({
        message: "Invoices unlinked successfully",
      });
      confirmUnlinkInvoicesModal.close();
    } catch (error) {
      alertToast({
        message: (error as BackendError).data?.error?.message,
      });
    }
  };

  const confirmUnlinkBills = async () => {
    try {
      await unlinkInvoices({
        entityId: entityId,
        financial_record_type: linkedBillItems?.at(0)?.financial_record_type!,
        transactionId,
        groupId: group.uuid,
      }).unwrap();
      successToast({
        message: "Bills unlinked successfully",
      });
      confirmUnlinkBillsModal.close();
    } catch (error) {
      alertToast({
        message: (error as BackendError).data?.error?.message,
      });
    }
  };

  const subscriptionModal = useModal();
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.TRANSACTIONS
  );

  return (
    <Async.Root
      {...{
        isEmpty: false,
        isLoading: isLoading || isLinkedTxnDataLoading,
        isSuccess: isSuccess || isLinkedTxnDataSuccess,
      }}
    >
      <Async.Empty>
        <></>
      </Async.Empty>
      <Async.Success>
        <Tab.Root defaultValue="DETAILS" className="t-h-full">
          <div className="t-flex t-flex-col t-h-full">
            <DashboardContainer className="t-h-full">
              <DashboardContainer.Header>
                <div className="t-sticky t-top-0 t-z-header t-bg-white">
                  <Header
                    transactionId={transactionId}
                    ledgerEntryId={transaction?.transaction.ledger_entry?.uuid}
                    onDuplicate={onDuplicate}
                  />
                  <div className="t-px-5 t-pt-3 t-pb-4">
                    <Tab.List>
                      <Tab.Trigger value="DETAILS">Details</Tab.Trigger>
                      <Tab.Trigger value="ACTIVITY">Activity</Tab.Trigger>
                    </Tab.List>
                  </div>
                </div>
              </DashboardContainer.Header>
              <DashboardContainer.Content>
                <Tab.Content value="DETAILS">
                  <div className="t-p-4 t-flex t-gap-4 t-flex-col t-pb-24 t-pt-0">
                    <TransactionInfo transactionId={transactionId} />
                    <Accordion.Root
                      type="multiple"
                      defaultValue={["COMMENT", "INVOICE_AND_MEMO", "CATEGORY"]}
                      className="t-flex t-gap-4 t-flex-col"
                    >
                      {linkedInvoices &&
                        linkedInvoices.invoices &&
                        linkedInvoices.invoices.length > 0 && (
                          <Accordion.Item value="CATEGORY">
                            <Accordion.Trigger className="!t-text-body-sm !t-font-medium">
                              <div className="t-flex t-gap-3">
                                <span>Categorisation Details</span>
                              </div>
                            </Accordion.Trigger>
                            <Accordion.Content>
                              <InvoiceCategoryDetails
                                linkedInvoices={linkedInvoices}
                              />
                            </Accordion.Content>
                          </Accordion.Item>
                        )}
                      {transaction?.parent_txn && (
                        <Card.Root variant="secondary" borderRounded="lg">
                          <Card.Header sticky={false}>
                            <Card.Title>Categorisation</Card.Title>
                          </Card.Header>
                          <Card.Body>
                            <Category
                              transactionId={transactionId}
                              isDisabled
                            />
                            <div className="t-relative t-mt-8">
                              <Divider />
                              <div className="t-absolute t-top-1/2 t-left-1/2 -t-translate-x-1/2 -t-translate-y-1/2 t-flex t-items-center t-gap-1.5 t-px-2 t-py-1 t-rounded-full t-bg-neutral-0">
                                <Link />
                                <span className="t-text-body-sm t-text-text-60">
                                  Parent Transaction
                                </span>
                              </div>
                            </div>
                            <div className="t-mt-8">
                              <div className="t-flex t-justify-between t-items-start">
                                <div className="t-flex t-flex-col t-gap-1">
                                  <div className="t-flex t-items-center t-gap-2 t-text-body-sm t-text-text-100">
                                    <span className="t-text-body-sm">
                                      {dayjs(
                                        transaction.parent_txn.date
                                      ).format(DISPLAY_DATE)}
                                    </span>
                                    <span className="t-text-neutral-20">•</span>
                                    <span className="t-text-body-sm">
                                      <AmountSuperScript
                                        amount={transaction.parent_txn.amount}
                                      />
                                    </span>
                                  </div>
                                  <div className=" t-text-text-30 t-text-body-sm">
                                    {transaction.parent_txn.description}
                                  </div>
                                </div>
                                <div className="t-flex t-gap-2">
                                  <Button
                                    customType="link"
                                    size="small"
                                    onClick={() =>
                                      dispatch(
                                        openSlider(transaction.parent_txn.uuid)
                                      )
                                    }
                                  >
                                    View
                                  </Button>
                                  <div
                                    className={classNames({
                                      "t-pointer-events-none":
                                        !hasWritePermission,
                                    })}
                                  >
                                    <Button
                                      customType="link"
                                      size="small"
                                      onClick={() => {
                                        if (parentTransaction) {
                                          dispatch(
                                            openEditSplitTransactionModal(
                                              parentTransaction
                                            )
                                          );
                                        }
                                      }}
                                      disabled={!parentTransaction}
                                      className={classNames({
                                        "!t-text-text-30": !hasWritePermission,
                                      })}
                                    >
                                      Edit
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Card.Body>
                        </Card.Root>
                      )}
                      {transaction?.split_transactions &&
                      !(
                        linkedInvoices &&
                        linkedInvoices.invoices &&
                        linkedInvoices.invoices.length > 0
                      ) &&
                      !transaction?.parent_txn ? (
                        <PermissionBasedUI
                          align="start"
                          feature={CLASSIFICATION_FEATURE_ACCESS}
                          blockedUI={
                            <Card.Root variant="secondary" borderRounded="lg">
                              <Card.Header sticky={false}>
                                <Card.Title>
                                  Categorisation & Classifications{" "}
                                  <DiamondPurple height={13} width={14} />
                                </Card.Title>
                                <div
                                  className="t-flex t-gap-1.5 t-text-purple t-items-center t-text-button-sm t-cursor-pointer"
                                  onClick={() => subscriptionModal.open()}
                                >
                                  Upgrade
                                </div>
                              </Card.Header>
                            </Card.Root>
                          }
                        >
                          <SplitTransactionDetail
                            transactionId={transactionId}
                          />
                        </PermissionBasedUI>
                      ) : (
                        <PermissionBasedUI
                          align="start"
                          feature={CLASSIFICATION_FEATURE_ACCESS}
                          blockedUI={
                            <Card.Root variant="secondary" borderRounded="lg">
                              <Card.Header sticky={false}>
                                <Card.Title>
                                  Classifications{" "}
                                  <DiamondPurple height={13} width={14} />
                                </Card.Title>
                                <div
                                  className="t-flex t-gap-1.5 t-text-purple t-items-center t-text-button-sm t-cursor-pointer"
                                  onClick={() => subscriptionModal.open()}
                                >
                                  Upgrade
                                </div>
                              </Card.Header>
                            </Card.Root>
                          }
                          showTooltip={false}
                        >
                          <Classifications
                            transactionId={transactionId}
                            entityId={entityId}
                          />
                        </PermissionBasedUI>
                      )}
                      <BooksSoftwareSubscriptionsModal
                        open={subscriptionModal.isOpen}
                        onClose={() => subscriptionModal.close()}
                        entityId={entityId}
                        onProductSubscribe={() => subscriptionModal.close()}
                        modalDescription={
                          <ModalDescriptionWrapper value="Unlock Classifications and more with Books Standard Plan" />
                        }
                      />
                      {/* <Classifications
                        transactionId={transactionId}
                        entityId={entityId}
                      /> */}

                      <Accordion.Item value="INVOICE_AND_MEMO">
                        <Accordion.Trigger className="!t-text-body-sm !t-font-medium">
                          <div className="t-flex t-gap-3">
                            <span>
                              {isBillTransaction
                                ? "Bill & Memo"
                                : "Invoice & Memo"}
                            </span>
                            {transaction?.invoices.some(
                              (i) =>
                                i.file_data?.invoice_status === AUTO_MATCHED
                            ) && (
                              <Tag tagType="yellow" rounded icon={false}>
                                Review associated invoice(s)
                              </Tag>
                            )}
                          </div>
                        </Accordion.Trigger>
                        <Accordion.Content>
                          <span className="t-flex t-gap-4 t-flex-col">
                            <KeyDownWrapper>
                              <InfoItem label="Invoices">
                                <Invoices
                                  transactionId={transactionId}
                                  isBillTransaction={isBillTransaction}
                                />
                              </InfoItem>
                            </KeyDownWrapper>

                            {isCredit && !isCreditCard && (
                              <div className="t-flex t-justify-between t-py-1 t-px-3 t-items-center t-border t-border-solid t-rounded t-border-purple-10 t-bg-surface-purple">
                                <div className="t-text-body-sm">
                                  Link transaction to Inkle invoices
                                </div>
                                <ConditionalToolTip
                                  condition={
                                    isSplitTransaction &&
                                    "Cannot link with Inkle Invoice since it is a split transaction"
                                  }
                                >
                                  <div
                                    className={classNames({
                                      "t-pointer-events-none":
                                        !hasWritePermission,
                                    })}
                                  >
                                    <Button
                                      size="small"
                                      className={classNames({
                                        "!t-bg-neutral-20 !t-text-text-30 !t-border-none":
                                          !hasWritePermission,
                                      })}
                                      onClick={() => {
                                        const shouldShowWarning =
                                          categoryName === "Bank Transfer" ||
                                          categoryName === "Pay Down Credit";

                                        if (shouldShowWarning) {
                                          onConfirmLinkInvoice();
                                        } else {
                                          linkInkleInvoiceModal.open();
                                        }
                                      }}
                                      disabled={isSplitTransaction}
                                    >
                                      {!hasWritePermission && (
                                        <span className="t-mr-1 t-inline-flex">
                                          <Padlock />
                                        </span>
                                      )}
                                      Link
                                    </Button>
                                  </div>
                                </ConditionalToolTip>
                              </div>
                            )}

                            {isBillTransaction && (
                              <div className="t-flex t-justify-between t-py-1 t-px-3 t-items-center t-border t-border-solid t-rounded t-border-purple-10 t-bg-surface-purple">
                                <div className="t-text-body-sm">
                                  Link transaction to Inkle bills
                                </div>
                                <ConditionalToolTip
                                  condition={
                                    isSplitTransaction &&
                                    "Cannot link with Inkle Bill since it is a split transaction"
                                  }
                                >
                                  <div>
                                    <Button
                                      size="small"
                                      onClick={linkInkleBillModal.open}
                                      disabled={isSplitTransaction}
                                    >
                                      Link
                                    </Button>
                                  </div>
                                </ConditionalToolTip>
                              </div>
                            )}

                            <Divider />

                            {linkedInvoiceItems.length > 0 &&
                              hasLinkedInvoice && (
                                <>
                                  <KeyDownWrapper>
                                    <InfoItem
                                      label={
                                        <div className="t-flex t-justify-between t-items-center t-mb-3">
                                          <span>Linked Invoice(s)</span>
                                          <LockedUi
                                            feature={
                                              WRITE_PERMISSION_KEYS.TRANSACTIONS
                                            }
                                          >
                                            <div className="t-flex t-gap-1">
                                              <Modal.Root
                                                open={
                                                  confirmUnlinkInvoicesModal.isOpen
                                                }
                                                onOpenChange={
                                                  confirmUnlinkInvoicesModal.toggle
                                                }
                                              >
                                                {hasWrittenOffInvoice ? (
                                                  <ConditionalToolTip
                                                    condition={
                                                      WRITTEN_OFF_UNLINK_DISABLED_TOOLTIP
                                                    }
                                                  >
                                                    <span className="t-flex t-gap-1.5 t-items-center t-text-button-sm t-text-neutral-30 t-cursor-not-allowed">
                                                      Unlink all
                                                    </span>
                                                  </ConditionalToolTip>
                                                ) : (
                                                  <Modal.Trigger asChild>
                                                    <div className="t-flex t-gap-1.5 t-items-center t-text-button-sm t-text-purple t-cursor-pointer">
                                                      Unlink all
                                                    </div>
                                                  </Modal.Trigger>
                                                )}
                                                <Modal.Content size="small">
                                                  <Modal.Body>
                                                    <div>
                                                      <span className="t-text-orange t-flex t-items-center t-justify-center t-w-10 t-h-10 t-rounded-full t-shadow-[0px_0px_0px_4px_rgba(255,220,78,0.15)] t-border t-border-orange-20 t-border-solid t-bg-yellow-20">
                                                        <TriangleWarning />
                                                      </span>
                                                      <p className="t-mb-2 t-text-body-sm t-font-medium t-mt-5">
                                                        Unlink invoice?
                                                      </p>
                                                      <div className="t-text-body-sm t-text-text-30">
                                                        Unlinking the invoice
                                                        will change the invoice
                                                        status and will
                                                        uncategorize the
                                                        corresponding
                                                        transaction.
                                                      </div>
                                                    </div>
                                                  </Modal.Body>
                                                  <Modal.FooterButtonGroup>
                                                    <Modal.RawClose asChild>
                                                      <Button>Cancel</Button>
                                                    </Modal.RawClose>
                                                    <Button
                                                      isLoading={
                                                        unlinkingInvoice
                                                      }
                                                      disabled={
                                                        unlinkingInvoice
                                                      }
                                                      customType="primary"
                                                      onClick={
                                                        confirmUnlinkInvoices
                                                      }
                                                    >
                                                      Confirm
                                                    </Button>
                                                  </Modal.FooterButtonGroup>
                                                </Modal.Content>
                                              </Modal.Root>
                                            </div>
                                          </LockedUi>
                                        </div>
                                      }
                                    >
                                      <LinkedInvoices
                                        transactionId={transactionId}
                                        invoices={linkedInvoiceItems}
                                      />
                                    </InfoItem>
                                  </KeyDownWrapper>
                                  <Divider />
                                </>
                              )}

                            {linkedBillItems.length > 0 && hasLinkedBill && (
                              <>
                                <KeyDownWrapper>
                                  <InfoItem
                                    label={
                                      <div className="t-flex t-justify-between t-items-center t-mb-3">
                                        <span>Linked Bill(s)</span>
                                        <div className="t-flex t-gap-1">
                                          <Modal.Root
                                            open={
                                              confirmUnlinkBillsModal.isOpen
                                            }
                                            onOpenChange={
                                              confirmUnlinkBillsModal.toggle
                                            }
                                          >
                                            <Modal.Trigger asChild>
                                              <div className="t-flex t-gap-1.5 t-text-purple t-items-center t-text-button-sm t-cursor-pointer">
                                                Unlink all
                                              </div>
                                            </Modal.Trigger>
                                            <Modal.Content size="small">
                                              <Modal.Body>
                                                <div>
                                                  <span className="t-text-orange t-flex t-items-center t-justify-center t-w-10 t-h-10 t-rounded-full t-shadow-[0px_0px_0px_4px_rgba(255,220,78,0.15)] t-border t-border-orange-20 t-border-solid t-bg-yellow-20">
                                                    <TriangleWarning />
                                                  </span>
                                                  <p className="t-mb-2 t-text-body-sm t-font-medium t-mt-5">
                                                    Unlink bill?
                                                  </p>
                                                  <div className="t-text-body-sm t-text-text-30">
                                                    Unlinking the bill will
                                                    change the bill status and
                                                    will uncategorize the
                                                    corresponding transaction.
                                                  </div>
                                                </div>
                                              </Modal.Body>
                                              <Modal.FooterButtonGroup>
                                                <Modal.RawClose asChild>
                                                  <Button>Cancel</Button>
                                                </Modal.RawClose>
                                                <Button
                                                  isLoading={unlinkingInvoice}
                                                  disabled={unlinkingInvoice}
                                                  customType="primary"
                                                  onClick={confirmUnlinkBills}
                                                >
                                                  Confirm
                                                </Button>
                                              </Modal.FooterButtonGroup>
                                            </Modal.Content>
                                          </Modal.Root>
                                        </div>
                                      </div>
                                    }
                                  >
                                    <LinkedBills
                                      transactionId={transactionId}
                                      invoices={linkedBillItems}
                                    />
                                  </InfoItem>
                                </KeyDownWrapper>
                                <Divider />
                              </>
                            )}

                            <KeyDownWrapper>
                              <Memo transactionId={transactionId} />
                            </KeyDownWrapper>
                          </span>
                        </Accordion.Content>
                      </Accordion.Item>

                      <Comments transactionId={transactionId} />
                    </Accordion.Root>
                  </div>
                  {Boolean(sendToChat) && (
                    <div className="t-p-4 t-bg-surface t-fixed t-bottom-0 t-shadow-tasks-slider-bottom t-w-full t-max-w-[600px]">
                      <Button
                        customType="primary"
                        onClick={() => sendToChat?.()}
                        block
                      >
                        Send to Chat
                      </Button>
                    </div>
                  )}
                  {linkInkleInvoiceModal.isOpen && (
                    <LinkInkleInvoiceModal
                      transactionId={transactionId}
                      isOpen={linkInkleInvoiceModal.isOpen}
                      close={linkInkleInvoiceModal.close}
                    />
                  )}
                  {linkInkleInvoiceModalEdit.isOpen && (
                    <LinkInkleInvoiceModal
                      transactionId={transactionId}
                      isOpen={linkInkleInvoiceModalEdit.isOpen}
                      close={linkInkleInvoiceModalEdit.close}
                    />
                  )}
                  {linkInkleBillModal.isOpen && (
                    <LinkInkleBillModal
                      transactionId={transactionId}
                      isOpen={linkInkleBillModal.isOpen}
                      close={linkInkleBillModal.close}
                    />
                  )}
                  <ChangeCategory
                    isOpen={changeCategoryModal.isOpen}
                    close={changeCategoryModal.close}
                    onConfirm={handleConfirmChangeCategory}
                    categoryName={categoryName}
                  />
                </Tab.Content>
                <Tab.Content value="ACTIVITY" className="t-h-full" asChild>
                  <AuditlogList entityId={entityId} objectId={transactionId} />
                </Tab.Content>
              </DashboardContainer.Content>
            </DashboardContainer>
          </div>
        </Tab.Root>
      </Async.Success>
    </Async.Root>
  );
};

export const TransactionSlider = ({
  sendToChat,
  onDuplicate,
}: {
  sendToChat?: ({ transactionId }: { transactionId: string }) => void;
  onDuplicate?: (transaction: Transaction) => void;
}) => {
  const dispatch = useAppDispatch();

  const { sliderTransactions } = useSelector(
    (state: RootState) => state.transactions
  );
  const { showSplitTransactionModal } = useSelector(
    (state: RootState) => state.splitTransaction
  );
  const { update } = useUpdateQuery();

  const onOpenChange = (transactionId: string) => {
    dispatch(closeSlider(transactionId));
    update({
      query: "transaction_id",
      value: null,
    });
  };

  return (
    <>
      {sliderTransactions.map(({ transactionId, animationId }) => (
        <Slider.Root
          key={animationId}
          open={Boolean(transactionId)}
          onOpenChange={() => onOpenChange(transactionId)}
        >
          <Slider.Content
            open={Boolean(transactionId)}
            useCustomOverlay
            className="t-h-full"
          >
            <TransactionBody
              sendToChat={
                sendToChat ? () => sendToChat?.({ transactionId }) : null
              }
              transactionId={transactionId}
              onDuplicate={onDuplicate}
            />
          </Slider.Content>
        </Slider.Root>
      ))}
      {showSplitTransactionModal && (
        <SplitTransaction isOpen={showSplitTransactionModal} />
      )}
    </>
  );
};
